import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteDocumentModal from "~/components/Document/deleteDocumentModal";
import { TOAST_MESSAGES } from "~/constants/messages";
import { DocumentApi } from "~/functions/api/document";
import { dateToString } from "~/functions/datetime";
import ContextMenu from "~/images/context_menu.svg?react";
import type { Document } from "~/interface/document";
import { setCurrentProject } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

const GenerationHistory = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth);
  const documentApi = useMemo(() => new DocumentApi(), []);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const segments = location.pathname.split("/");
  const documentType = segments[segments.length - 2];

  // Get project members from Redux store
  const projectMembers = useSelector((state: RootState) => state.auth.projectMembers);

  // Function to check if current user can manage documents
  const canManageDocuments = (): boolean => {
    if (!projectMembers || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member: any) => member.user.id === currentUser?.id,
    );
    const canManage =
      currentUserInProject?.role === "owner" || currentUserInProject?.role === "contributor";
    return canManage;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const documentName = useMemo(() => {
    const map: Record<string, string> = {
      "system-proposal": "システム概要書",
      estimation: "概算工数見積り",
      "plugin-proposal": "プラグイン提案書",
      "functional-requirements": "機能要件書",
      "non-functional-requirements": "非機能要件書",
      "field-design": "フィールド設計書",
      "app-design": "アプリ設計書",
      workflow: "業務フロー図",
      "entity-relationship": "ER図",
      "system-architecture": "システム構成図",
      "customize-specification": "カスタマイズ仕様書",
    };
    return map[documentType!] ?? documentType;
  }, [documentType]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleBreadcrumbToDetail = () => {
    const documentId = currentProject.outputs?.[documentType!]?.id;
    if (currentUser?.organization_id && currentProject?.id && documentId) {
      navigate(
        `/${currentUser.organization_id}/project/${currentProject.id}/${documentType}/${documentId}`,
      );
    }
  };

  const handleGetAllDocuments = useCallback(async (): Promise<Document[] | null> => {
    if (!currentProject) {
      navigate(`/${currentUser.organization_id}/home`);
      return null;
    }

    if (!currentProject.id) {
      toast.error(TOAST_MESSAGES.HISTORY.DOCUMENT_NOT_FOUND(documentName ?? ""));
      return null;
    }

    if (!projectId) {
      toast.error(TOAST_MESSAGES.HISTORY.UNKNOWN_DOCUMENT_TYPE);
      return null;
    }

    const response = await documentApi.getAllByType(
      currentUser.organization_id,
      currentProject.id,
      documentType!,
    );
    if (response !== null) {
      return response;
    } else {
      return null;
    }
  }, [
    currentProject,
    currentUser?.organization_id,
    documentType,
    navigate,
    documentApi,
    documentName,
    projectId,
  ]);

  const handleRollbackDocument = async (document: Document) => {
    const response = await documentApi.rollback(
      currentUser.organization_id,
      currentProject.id,
      document.id,
    );
    if (response) {
      toast.success(TOAST_MESSAGES.HISTORY.RESTORE_SUCCESS);
      handleRollbackSuccess(document);
    } else {
      toast.error(TOAST_MESSAGES.HISTORY.RESTORE_FAILED);
    }
  };

  const handleRollbackSuccess = (document: Document) => {
    const newOutputs = {
      ...currentProject.outputs,
      [documentType!]: {
        ...currentProject.outputs?.[documentType!],
        id: document.id,
      },
    };

    dispatch(
      setCurrentProject({
        ...currentProject,
        outputs: newOutputs,
      }),
    );
    setDocuments((prevDocuments) => [
      document,
      ...prevDocuments.filter((doc) => doc.id !== document.id),
    ]);
  };

  const handleDeleteSuccess = (deletedId: string) => {
    setDocuments((prevDocuments) => prevDocuments.filter((doc) => doc.id !== deletedId));
    setIsDeleteModalOpen(false);
    setSelectedDocument(null);
  };

  useEffect(() => {
    if (!currentProject) {
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    const fetchAllDocuments = async () => {
      const response = await handleGetAllDocuments();
      if (response !== null) {
        setDocuments(response);
      } else {
        setDocuments([]);
      }
    };

    fetchAllDocuments();
  }, [currentProject, currentUser, handleGetAllDocuments, navigate]);

  return (
    <div className={styles["history"]} style={{ position: "relative" }}>
      <div className={styles["header"]}>
        <div className={styles["breadcrumb"]}>
          <span
            className={styles["breadcrumbItem"]}
            role="button"
            tabIndex={0}
            onClick={handleBreadcrumbToDetail}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBreadcrumbToDetail();
              }
            }}
          >
            {documentName}
          </span>
          <span className={styles["breadcrumbSeparator"]}>＞</span>
          <span className={styles["breadcrumbItem"]}>生成履歴</span>
        </div>
        <div className={styles["header__title"]}>
          <ArrowBackIcon
            {...(styles["icon"] && { className: styles["icon"] })}
            onClick={handleGoBack}
          />
          <h2>生成履歴</h2>
        </div>
      </div>
      <div className={styles["body"]} data-testid="generation-history">
        <table className={styles["table"]}>
          <thead className={styles["body__header"]}>
            <tr>
              <th>生成日時</th>
              <th colSpan={2}>更新者</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => {
              const isCurrentVersion = currentProject.outputs?.[documentType!]?.id === document.id;

              return isCurrentVersion ? (
                // 現在のバージョンの場合の <tr>
                <tr
                  key={document.id}
                  className={styles["current-version"]}
                  onClick={() =>
                    navigate(
                      `/${currentUser.organization_id}/project/${currentProject.id}/${documentType}/${currentProject.outputs?.[documentType!]?.id}`,
                    )
                  }
                >
                  <td>
                    <p>{dateToString(document.created_at)}</p>
                    <label>現在のバージョン</label>
                  </td>
                  <td>
                    <AccountCircleIcon style={{ fontSize: 40, color: "#1D9FD6" }} />
                    {document.modified_by ? document.modified_by.display_name : "REQXIGN"}
                  </td>
                  <td>
                    <div
                      className={styles["dropdownWrapper"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(document.id);
                      }}
                    >
                      <ContextMenu />
                      {dropdownOpen === document.id && (
                        <div
                          className={styles["dropdownMenu"]}
                          ref={dropdownOpen === document.id ? dropdownRef : null}
                        >
                          <ul>
                            <li
                              className={`${styles["danger"]} ${styles["disabled"]}`}
                              data-tooltip="最新バージョンのドキュメントは削除できません"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              削除
                            </li>
                            <li
                              className={styles["cancelOption"]}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDropdownOpen(null);
                              }}
                            >
                              キャンセル
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                // それ以外のバージョンの場合の <tr>
                <tr
                  key={document.id}
                  className={styles["past-version"]}
                  onClick={() =>
                    navigate(
                      `/${currentUser.organization_id}/project/${currentProject.id}/${documentType}/history/${document.id}`,
                    )
                  }
                >
                  <td>
                    <p>{dateToString(document.created_at)}</p>
                  </td>
                  <td>
                    <AccountCircleIcon style={{ fontSize: 40, color: "#1D9FD6" }} />
                    {document.modified_by ? document.modified_by.display_name : "REQXIGN"}
                  </td>
                  <td>
                    <div
                      className={styles["dropdownWrapper"]}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(document.id);
                      }}
                    >
                      <ContextMenu />
                      {dropdownOpen === document.id && (
                        <div
                          className={styles["dropdownMenu"]}
                          ref={dropdownOpen === document.id ? dropdownRef : null}
                        >
                          <ul>
                            <li
                              className={`${!canManageDocuments() ? styles["disabled"] : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canManageDocuments()) {
                                  handleRollbackDocument(document);
                                }
                              }}
                            >
                              復元
                            </li>
                            <li
                              className={`${styles["danger"]} ${!canManageDocuments() ? styles["disabled"] : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canManageDocuments()) {
                                  setSelectedDocument(document);
                                  setIsDeleteModalOpen(true);
                                  setDropdownOpen(null);
                                }
                              }}
                            >
                              削除
                            </li>
                            <li
                              className={styles["cancelOption"]}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDropdownOpen(null);
                              }}
                            >
                              キャンセル
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isDeleteModalOpen && selectedDocument && (
        <DeleteDocumentModal
          onClose={() => setIsDeleteModalOpen(false)}
          documentId={selectedDocument.id}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  );
};

export default GenerationHistory;
